import { Box, Button, Typography } from "@mui/material";
import { onlineManager } from "@tanstack/react-query";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  retryKey: number;
}

/** True when a lazy route module could not be downloaded or evaluated. */
export function isRouteChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    (msg.includes("failed to fetch") && msg.includes(".tsx"))
  );
}

export function getRouteChunkErrorCopy(online: boolean): {
  title: string;
  body: string;
  retryLabel: string;
} {
  if (online) {
    return {
      title: "This page failed to load",
      body: import.meta.env.DEV
        ? "The app could not load this screen's code. After editing files, reload the page so the browser picks up the latest modules from the dev server."
        : "The app could not load this screen. Reload the page to fetch the latest version.",
      retryLabel: "Reload page",
    };
  }

  return {
    title: "This page is not available offline",
    body: "On web, pages load in separate files. Visit this page once while online, or reconnect and try again.",
    retryLabel: "Try again",
  };
}

function RouteChunkErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { title, body, retryLabel } = getRouteChunkErrorCopy(
    onlineManager.isOnline()
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 4,
        minHeight: 240,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" component="h1">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" maxWidth={360}>
        {body}
      </Typography>
      <Button variant="contained" onClick={onRetry}>
        {retryLabel}
      </Button>
    </Box>
  );
}

/**
 * Catches lazy-route chunk load failures so navigating offline on web does not
 * white-screen the app. Native builds bundle assets locally and rarely hit this.
 */
export class RouteChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, retryKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (!isRouteChunkLoadError(error)) {
      throw error;
    }
    return { error };
  }

  handleRetry = () => {
    if (onlineManager.isOnline()) {
      window.location.reload();
      return;
    }

    this.setState((prev) => ({ error: null, retryKey: prev.retryKey + 1 }));
  };

  render() {
    if (this.state.error) {
      return <RouteChunkErrorFallback onRetry={this.handleRetry} />;
    }

    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}
